import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EvaluationPhaseForm from './EvaluationPhaseForm';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useState } from 'react';

const EvaluationGroupCard = ({ group, stateNameMap = {}, workflowTemplate = null, onSuccess, onError }) => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Build state order from workflow transitions
  const buildStateOrder = () => {
    if (!workflowTemplate || !workflowTemplate.transitions) {
      return {};
    }

    const stateOrder = {};
    let order = 0;

    // Get initial state ID from workflow template
    const initialStateId = workflowTemplate.initial_state_id;
    if (!initialStateId) return {};

    // Build a map of from_state_id -> to_state_id from transitions
    const transitionMap = {};
    workflowTemplate.transitions.forEach(transition => {
      transitionMap[transition.from_state_id] = transition.to_state_id;
    });

    // Follow the transition chain starting from initial state
    let currentStateId = initialStateId;
    const visited = new Set();

    while (currentStateId && !visited.has(currentStateId)) {
      stateOrder[currentStateId] = order++;
      visited.add(currentStateId);
      currentStateId = transitionMap[currentStateId];
    }

    return stateOrder;
  };

  // Helper function to get display name for phase
  const getPhaseDisplayName = (phase, index) => {
    if (phase.is_challenge_phase) {
      return 'Challenge';
    }
    if (phase.workflow_state_id && stateNameMap[phase.workflow_state_id]) {
      // Format state name: "review_l1" -> "Review L1"
      return stateNameMap[phase.workflow_state_id]
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return `Phase ${index + 1}`;
  };

  // Get sorted phases
  const getSortedPhases = () => {
    const stateOrder = buildStateOrder();

    return group.evaluation_phases
      .filter(phase => {
        // Exclude phases that have SEND_FOR_AI_EVALUATION in their lifecycle actions
        const hasAiEvaluation = phase.lifecycle_actions?.some(la => la.action === 'SEND_FOR_AI_EVALUATION') ||
          workflowTemplate?.states?.find(s => s.id === phase.workflow_state_id)
            ?.lifecycle_actions?.some(la => la.action === 'SEND_FOR_AI_EVALUATION');

        if (hasAiEvaluation) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        const orderA = stateOrder[a.workflow_state_id] ?? Number.MAX_SAFE_INTEGER;
        const orderB = stateOrder[b.workflow_state_id] ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
  };

  return (
    <Card sx={{ mb: 1, border: 'none', boxShadow: 'none' }}>
      <CardContent sx={{ p: '0 !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Evaluation group name : {group.name}
          </Typography>
        </Box>
        {getSortedPhases().map((phase, index) => (
          <Accordion
            key={phase.id}
            expanded={expanded === phase.id}
            onChange={handleChange(phase.id)}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: '1px solid #eee',
              mb: 1,
              borderRadius: 1
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel-${phase.id}-content`}
              id={`panel-${phase.id}-header`}
              sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { margin: '12px 0' } }}
            >
              <Typography variant="body2" fontWeight="medium">
                {getPhaseDisplayName(phase, index)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <EvaluationPhaseForm
                phase={phase}
                group={group}
                phaseIndex={index}
                onSuccess={onSuccess}
                onError={onError}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
};

export default EvaluationGroupCard;

